import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { DollarSign, Building2, Calendar, Plane, Coffee, Luggage, ShieldCheck, MapPin, Clock, ChevronUp, ChevronDown } from 'lucide-react';

const QuoteCard = ({ quote, onAcceptQuote }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatPrice = (amount, currency) => {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return formatter?.format(amount);
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Fix: Access metadata from quote.metadata instead of quote.quote_metadata
  const metadata = quote?.metadata || {};

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft hover:shadow-elevated transition-all duration-200 mb-4">
      {/* Card Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Agent Information */}
            {(metadata?.agentFullName || metadata?.agentCompanyName) && (
              <div className="mb-3 pb-3 border-b border-border">
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="Building2" size={16} className="text-muted-foreground" />
                  <div>
                    {metadata?.agentCompanyName && (
                      <p className="font-medium text-foreground">{metadata?.agentCompanyName}</p>
                    )}
                    {metadata?.agentFullName && (
                      <p className="text-muted-foreground">{metadata?.agentFullName}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center space-x-3 mb-2">
              <div className="flex items-center space-x-2">
                <Icon name="DollarSign" size={20} className="text-primary" />
                <h3 className="text-lg font-semibold text-foreground">
                  {formatPrice(quote?.quote_price, quote?.quote_currency)}
                </h3>
              </div>
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                Quote Received
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Clock" size={14} />
              <span>Sent {formatDateTime(quote?.quote_submitted_at)}</span>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
          </button>
        </div>
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="p-4 space-y-4 border-t border-border">
          {/* Hotel/Accommodation */}
          {metadata?.hotelName && (
            <div className="flex items-start space-x-3">
              <Icon name="Building2" size={18} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Accommodation</p>
                <p className="text-base text-foreground mt-1">{metadata?.hotelName}</p>
                {metadata?.boardBasis && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {metadata?.boardBasis?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase())}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Dates */}
          {metadata?.dates && (
            <div className="flex items-start space-x-3">
              <Icon name="Calendar" size={18} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Travel Dates</p>
                {metadata?.dates?.flexible ? (
                  <p className="text-base text-foreground mt-1">Flexible dates</p>
                ) : metadata?.dates?.startDate ? (
                  <p className="text-base text-foreground mt-1">
                    {formatDate(metadata?.dates?.startDate)} - {formatDate(metadata?.dates?.endDate)}
                  </p>
                ) : null}
              </div>
            </div>
          )}

          {/* Flights */}
          {metadata?.flights?.included && (
            <div className="flex items-start space-x-3">
              <Icon name="Plane" size={18} className="text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Flights</p>
                <p className="text-base text-foreground mt-1">Flights included</p>
                {metadata?.flights?.departureAirport && (
                  <p className="text-sm text-muted-foreground mt-1">
                    <Icon name="MapPin" size={12} className="inline mr-1" />
                    From: {metadata?.flights?.departureAirport}
                  </p>
                )}
                {metadata?.flights?.airline && (
                  <p className="text-sm text-muted-foreground">
                    Airline: {metadata?.flights?.airline}
                  </p>
                )}
                {metadata?.flights?.flightTimes && (
                  <p className="text-sm text-muted-foreground">
                    <Icon name="Clock" size={12} className="inline mr-1" />
                    {metadata?.flights?.flightTimes}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Inclusions Grid */}
          {(metadata?.transferIncluded || metadata?.baggageIncluded) && (
            <div>
              <p className="text-sm font-medium text-foreground mb-3">What's Included</p>
              <div className="grid grid-cols-2 gap-3">
                {metadata?.transferIncluded && (
                  <div className="flex items-center space-x-2 p-3 bg-success/10 rounded-lg border border-success/20">
                    <Icon name="ShieldCheck" size={18} className="text-success flex-shrink-0" />
                    <span className="text-sm text-foreground">Airport Transfer</span>
                  </div>
                )}
                {metadata?.baggageIncluded && (
                  <div className="flex items-center space-x-2 p-3 bg-accent/10 rounded-lg border border-accent/20">
                    <Icon name="Luggage" size={18} className="text-accent flex-shrink-0" />
                    <span className="text-sm text-foreground">Baggage Allowance</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Inclusions */}
          {metadata?.additionalInclusions && (
            <div className="flex items-start space-x-3">
              <Icon name="Coffee" size={18} className="text-warning mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Additional Inclusions</p>
                <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">
                  {metadata?.additionalInclusions}
                </p>
              </div>
            </div>
          )}

          {/* Additional Notes */}
          {metadata?.notes && (
            <div className="pt-3 border-t border-border">
              <p className="text-sm font-medium text-foreground mb-2">Additional Notes</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {metadata?.notes}
              </p>
            </div>
          )}
        </div>
      )}
      {/* Action Buttons */}
      <div className="p-4 border-t border-border">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="default"
            size="sm"
            iconName="Check"
            iconPosition="left"
            onClick={() => onAcceptQuote?.(quote)}
          >
            Accept Quote
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
          >
            Discuss Quote
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuoteCard;